// Thin fetch wrapper for the iframe → Sigil backend calls. Always sends the
// publishable key in `X-Sigil-Publishable-Key`; sends `Authorization: Bearer`
// when we already have a token.

const API_URL = (import.meta.env.VITE_API_BASE_URL as string) ?? 'http://localhost:8081';

export type ApiContext = {
  publishableKey: string;
  /** Either an OIDC JWT supplied by the parent or a Sigil-issued JWT after OTP. */
  token: string | null;
};

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function call<T>(ctx: ApiContext, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('X-Sigil-Publishable-Key', ctx.publishableKey);
  if (ctx.token) headers.set('Authorization', `Bearer ${ctx.token}`);

  const res = await fetch(API_URL + path, { ...init, headers });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* keep null */
  }

  if (!res.ok) {
    const code = (parsed as any)?.error?.code ?? 'http_error';
    const message = (parsed as any)?.error?.message ?? `request failed: ${res.status}`;
    throw new ApiError(res.status, code, message);
  }
  return ((parsed as any)?.data ?? parsed) as T;
}

// ---- Endpoints ----

export type Identify = {
  organization_id: string;
  user_identity_id: string;
  auth_provider: string;
  provider_user_id: string;
  email?: string;
  supported_networks?: string[];
  signing_mode?: string;
};

export type WalletDTO = {
  id: string;
  organization_id: string;
  user_identity_id: string;
  address: string;
  public_key: string;
  chain_type: string;
  curve: string;
  status: string;
  created_at: string;
  account_type: 'eoa' | 'smart';
  signer_address?: string;
  smart_account?: string;
};

export type ProviderShareDTO = {
  wallet_id: string;
  provider_share: string; // base64
  share_index: number;
};

export type VerifyOtpDTO = {
  token: string;
  user_identity_id: string;
  organization_id: string;
  email: string;
};

export const api = {
  identify: (ctx: ApiContext) => call<Identify>(ctx, '/v1/wallets/identify', { method: 'POST' }),
  getWallet: (ctx: ApiContext) => call<WalletDTO>(ctx, '/v1/wallets/me', { method: 'GET' }),
  getAllWallets: (ctx: ApiContext) => call<WalletDTO[]>(ctx, '/v1/wallets/me/all', { method: 'GET' }),
  createWallet: (
    ctx: ApiContext,
    input: {
      address: string;
      public_key: string;
      chain_type: string;
      curve?: string;
      provider_share: string;
      provider_share_index: number;
      recovery_share: string;
      recovery_share_index: number;
      account_type?: 'eoa' | 'smart';
      signer_address?: string;
      smart_account?: string;
    },
  ) => call<WalletDTO>(ctx, '/v1/wallets', { method: 'POST', body: JSON.stringify(input) }),
  createWalletTEE: (ctx: ApiContext) =>
    call<WalletDTO>(ctx, '/v1/wallets', { method: 'POST', body: JSON.stringify({}) }),
  getProviderShare: (ctx: ApiContext, curve?: string) =>
    call<ProviderShareDTO>(ctx, `/v1/wallets/me/shares/provider${curve ? `?curve=${curve}` : ''}`, { method: 'POST' }),
  startOtp: (ctx: ApiContext, email: string) =>
    call<null>(ctx, '/v1/wallets/auth/email-otp/start', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyOtp: (ctx: ApiContext, email: string, code: string) =>
    call<VerifyOtpDTO>(ctx, '/v1/wallets/auth/email-otp/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),
  initiateRecovery: (ctx: ApiContext) =>
    call<{ recovery_process_id: string }>(ctx, '/v1/wallets/me/recovery/initiate', { method: 'POST' }),
  verifyRecovery: (ctx: ApiContext, processId: string, code: string) =>
    call<{
      wallet_id: string;
      provider_share: string;
      recovery_share: string;
      polynomial_version: number;
    }>(ctx, `/v1/wallets/me/recovery/${processId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  completeRecovery: (
    ctx: ApiContext,
    processId: string,
    input: {
      new_provider_share: string;
      new_provider_share_index: number;
      new_recovery_share: string;
      new_recovery_share_index: number;
    },
  ) =>
    call<null>(ctx, `/v1/wallets/me/recovery/${processId}/complete`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // TEE-mode signing: the TEE holds both shares, so the iframe just sends the
  // signing request without any key material.
  sign: (ctx: ApiContext, body: {
    sign_type: string;
    network?: string;
    message?: string;
    device_share?: string;
    curve?: string;
  }) =>
    call<{ signature: string; digest?: string }>(ctx, '/v1/wallets/me/sign', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Gas-station endpoints — only the iframe calls them (the JWT lives here).
  userOpBuild: (
    ctx: ApiContext,
    body: {
      chain: string;
      calls: Array<{ to: string; data: string; value?: string }>;
      preview?: { title: string; description?: string; fields?: { label: string; value: string }[] };
    },
  ) =>
    call<{
      user_op_hash: string;
      preview: { title: string; description?: string; fields?: { label: string; value: string }[] };
      expires_at: string;
    }>(ctx, '/v1/userop/build', { method: 'POST', body: JSON.stringify(body) }),
  userOpSubmit: (ctx: ApiContext, body: { user_op_hash: string; signature: string }) =>
    call<{
      user_op_hash: string;
      tx_hash?: string;
      success?: boolean;
      block_number?: string;
      status: 'pending' | 'submitted' | 'confirmed' | 'failed';
    }>(ctx, '/v1/userop/submit', { method: 'POST', body: JSON.stringify(body) }),
  userOpGet: (ctx: ApiContext, hash: string) =>
    call<{
      user_op_hash: string;
      tx_hash?: string;
      success?: boolean;
      block_number?: string;
      status: 'pending' | 'submitted' | 'confirmed' | 'failed';
    }>(ctx, `/v1/userop/${hash}`, { method: 'GET' }),

  // Raw EVM tx lane (EOA path). The backend prepares the unsigned tx
  // (nonce + gas + chain id), the iframe signs locally with the EOA key,
  // and the backend broadcasts the signed envelope via its EVM RPC.
  prepareEvmTx: (
    ctx: ApiContext,
    body: { chain: string; to: string; data?: string; value?: string; gas_limit?: string },
  ) =>
    call<{
      chain: string;
      chain_id: string;
      nonce: string;
      from: string;
      to: string;
      data: string;
      value: string;
      gas_limit: string;
      max_fee_per_gas: string;
      max_priority_fee_per_gas: string;
    }>(ctx, '/v1/wallets/evm/prepare-tx', { method: 'POST', body: JSON.stringify(body) }),
  broadcastEvmTx: (ctx: ApiContext, body: { chain: string; raw_tx: string }) =>
    call<{ chain: string; chain_id: string; tx_hash: string }>(
      ctx,
      '/v1/wallets/evm/broadcast-tx',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ERC-4337 smart-account provisioning. One row per (wallet, chain).
  // The address is counterfactual until the first sponsored UserOp lands;
  // sendTransaction picks the smart-account lane automatically once a row
  // exists for the requested chain.
  listSmartAccounts: (ctx: ApiContext) =>
    call<{ items: SmartAccountDTO[] }>(ctx, '/v1/wallets/me/smart-accounts', { method: 'GET' }),
  createSmartAccount: (ctx: ApiContext, body: { chain: string }) =>
    call<SmartAccountDTO>(ctx, '/v1/wallets/me/smart-accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // WalletConnect sign-request lifecycle. Both endpoints return 204
  // on success — there's no body the iframe needs back.
  completeWCRequest: (ctx: ApiContext, id: string, result: string) =>
    call<null>(ctx, `/v1/wallets/me/wc/requests/${id}/sign`, {
      method: 'POST',
      body: JSON.stringify({ result }),
    }),
  rejectWCRequest: (ctx: ApiContext, id: string, code: string, message?: string) =>
    call<null>(ctx, `/v1/wallets/me/wc/requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ code, message: message ?? '' }),
    }),
};

// WalletConnect — shell pair entry point. The shell (or playground)
// captures a wc: URI from the miniapp; the iframe forwards it here
// to /v1/wallets/me/wc/pair with the user's JWT.
export const apiPairWCFromShell = (ctx: ApiContext, uri: string) =>
  call<{ pairing_topic: string; wallet_id: string }>(ctx, '/v1/wallets/me/wc/pair', {
    method: 'POST',
    body: JSON.stringify({ uri }),
  });

export type WCSessionDTO = {
  topic: string;
  organization_id: string;
  user_identity_id: string;
  wallet_id: string;
  peer_name?: string;
  peer_url?: string;
  peer_icon?: string;
  chains: string[];
  status: string;
  created_at: string;
};

export const apiListWCSessions = (ctx: ApiContext) =>
  call<{ items: WCSessionDTO[] }>(ctx, '/v1/wallets/me/wc/sessions', { method: 'GET' });

export const apiDisconnectWCSession = (ctx: ApiContext, topic: string) =>
  call<null>(ctx, `/v1/wallets/me/wc/sessions/${encodeURIComponent(topic)}`, { method: 'DELETE' });

// WC SSE event payloads (mirrors signing_request_dto on the backend).
export type WCSigningRequestEvent = {
  id: string;
  topic: string;
  wc_request_id: string;
  method: 'personal_sign' | 'eth_sign' | 'eth_signTypedData_v4' | 'eth_sendTransaction' | 'wallet_switchEthereumChain';
  params: { message?: string; digest_hex?: string; tx?: unknown; chain?: string };
  status: string;
  created_at: string;
};

// openWCRequestStream connects to the SSE endpoint and hands each
// `request` event to the consumer as a parsed object. Returns a
// close() — call it on unmount or auth change.
export function openWCRequestStream(
  ctx: ApiContext,
  onRequest: (req: WCSigningRequestEvent) => void,
): () => void {
  if (!ctx.token) {
    return () => {};
  }
  // Native EventSource can't send custom headers (no Authorization),
  // so we use fetch + ReadableStream to drive the SSE protocol
  // ourselves. Cancel by closing the AbortController.
  const ctrl = new AbortController();
  void streamWC(ctx, ctrl.signal, onRequest);
  return () => ctrl.abort();
}

async function streamWC(
  ctx: ApiContext,
  signal: AbortSignal,
  onRequest: (req: WCSigningRequestEvent) => void,
) {
  while (!signal.aborted) {
    try {
      const res = await fetch(API_URL + '/v1/wallets/me/wc/requests', {
        method: 'GET',
        headers: {
          'X-Sigil-Publishable-Key': ctx.publishableKey,
          Authorization: `Bearer ${ctx.token}`,
          Accept: 'text/event-stream',
        },
        signal,
      });
      if (!res.ok || !res.body) {
        // Backoff and retry — the channel may be 503 wc_disabled or
        // the deploy was just rolling. 5s is enough for the latter.
        await sleep(5_000);
        continue;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const block = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const parsed = parseSSEBlock(block);
          if (parsed?.event === 'request' && parsed.data) {
            try {
              onRequest(JSON.parse(parsed.data) as WCSigningRequestEvent);
            } catch { /* malformed; skip */ }
          }
        }
      }
    } catch {
      if (signal.aborted) return;
      await sleep(2_000);
    }
  }
}

function parseSSEBlock(block: string): { event?: string; data?: string } | null {
  const out: { event?: string; data?: string } = {};
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) out.event = line.slice(6).trim();
    else if (line.startsWith('data:')) out.data = (out.data ?? '') + line.slice(5).trim();
  }
  return out.event ? out : null;
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export type SmartAccountDTO = {
  id: string;
  wallet_id: string;
  owner_address: string;
  chain: string;
  factory: string;
  address: string;
  deployed: boolean;
  sponsor_policy?: string;
  created_by: string;
  created_at: string;
};
